import * as dateFns from "date-fns";
import { fromZonedTime } from "date-fns-tz";

import { Constants, getRunningTimeMinutes } from "@eagerdog/constants";
import {
  IEvent,
  IShow,
  IScoreRegistrationSummary,
  IRunningOrderDogClass,
  IAocScheduleDay,
  IAocScheduleColumn,
  IAocScheduleItem,
  IAocScheduleDogEntry,
} from "@eagerdog/interfaces";

/**
 * One-off, event-specific "interwoven" judging schedule computation for event
 * handle "2az1nm" (AOC September Scentwork Trial) - a second, unrelated
 * one-off customer request, similar in spirit to the earlier "wyip3b" one
 * (see wyip3b-interwoven-schedule.util.ts).
 *
 * Unlike wyip3b (which hardcoded the entire class order/judges/breaks in a
 * TS config), this event's Judge Schedule tab is configured with the real
 * desired structure: each show has Ring 1 (one element, e.g. Container) and
 * Ring 2 (the other element, e.g. Buried), each with its own judge and its
 * own class order + break tiles, matching what the secretary wants. This
 * function just MERGES ring-by-ring across a day's two trial shows into one
 * printed column per ring - order, judge names, and break placement/duration
 * are all read live from that ring data, so this can never drift out of sync
 * with what's shown/edited in the Judge Schedule tab. Only two small pieces
 * are genuinely not derivable from a single ring/show in isolation, and are
 * captured as tiny explicit config below:
 *   1. Novice A + Novice B collapse into one printed "Novice" line.
 *   2. Which canonical levels merge Trial 1 + Trial 2 into one printed block
 *      ("Novice", "Advanced") vs. run as two separate sequential blocks
 *      ("Excellent", "Master") - a scheduling choice made by the secretary,
 *      not encoded anywhere else.
 */
export const AOC_INTERWOVEN_EVENT_HANDLE = "2az1nm";

// Secretary-requested fixed daily schedule (2026-09-14): the printed session
// (first class group) starts at 8:30 AM event-local time every trial day,
// with a Judges Briefing printed at 8:00 AM (30 minutes earlier). This is
// deliberately independent of each show's actual stored `show_date` time
// (which is left untouched, since it's used broadly elsewhere in the system)
// - only the calendar DATE is taken from show_date; the time-of-day for both
// of these is fixed here.
const SESSION_START_HOUR = 8;
const SESSION_START_MINUTE = 30;
const JUDGES_BRIEFING_MINUTES_BEFORE = 30;

// Builds a UTC Date instant representing the given hour/minute, in the given
// IANA time zone, on the same calendar day as `referenceDate` (also
// interpreted in that time zone) - e.g. for a 2026-09-19 18:15 UTC reference
// date in "America/Toronto" (which is 2026-09-19 local), with hour=8/minute=30,
// returns the UTC instant for 2026-09-19T08:30:00 America/Toronto.
function zonedTimeOnSameDay(
  referenceDate: Date,
  timeZone: string,
  hour: number,
  minute: number
): Date {
  const dateKey = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(referenceDate); // "YYYY-MM-DD"

  const hh = String(hour).padStart(2, "0");
  const mm = String(minute).padStart(2, "0");
  return fromZonedTime(`${dateKey}T${hh}:${mm}:00`, timeZone);
}

// Levels whose Trial 1 + Trial 2 occurrences merge into a single printed
// block (dogs from both trials listed together, in one time slot).
// Any canonical level not listed here runs as two separate sequential
// blocks - Trial 1's dogs fully listed, then Trial 2's.
const TRIAL_COMBINED_LEVELS = new Set(["Novice", "Advanced"]);

// Collapses AKC Scent Work's Novice A / Novice B sub-classes into a single
// printed "Novice" line - the secretary schedules and prints them as one
// combined block, even though they're distinct classes in the DB.
function canonicalLevel(level: string): string {
  return level.startsWith("Novice") ? "Novice" : level;
}

interface IRawGroup {
  canonicalLevel: string;
  levels: string[]; // constituent raw levels, e.g. ["Novice A", "Novice B"]
  breakBeforeMinutes?: number;
  breakLabel?: string;
}

// ---------------
// Walks a ring's dog_classes in stored order, collapsing consecutive tiles
// that share a canonical level (e.g. Novice A + Novice B) into one group, and
// attaching any break tile immediately preceding a group to that group.
function groupRingDogClasses(dogClasses: IRunningOrderDogClass[]): IRawGroup[] {
  const groups: IRawGroup[] = [];
  let pendingBreak: { minutes: number; label?: string } | undefined;

  for (const dc of dogClasses) {
    if (dc.type === Constants.running_order_dog_class_type.break) {
      pendingBreak = {
        minutes: dc.duration ?? Constants.running_order_times.break,
        label: dc.name,
      };
      continue;
    }

    if (!dc.level) continue;
    const canonical = canonicalLevel(dc.level);

    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.canonicalLevel === canonical && !pendingBreak) {
      lastGroup.levels.push(dc.level);
      continue;
    }

    groups.push({
      canonicalLevel: canonical,
      levels: [dc.level],
      breakBeforeMinutes: pendingBreak?.minutes,
      breakLabel: pendingBreak?.label,
    });
    pendingBreak = undefined;
  }

  return groups;
}

// ---------------
const compareArmbands = (a: IScoreRegistrationSummary, b: IScoreRegistrationSummary): number => {
  const getArmbandNum = (value?: string) => {
    const parsed = parseInt((value || "").replace(/[^0-9]/g, ""), 10);
    return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
  };

  const aArm = a.arm_number || "";
  const bArm = b.arm_number || "";
  const aNum = getArmbandNum(aArm);
  const bNum = getArmbandNum(bArm);

  if (aNum !== bNum) return aNum - bNum;
  return aArm.localeCompare(bArm, undefined, { numeric: true });
};

// ---------------
// Mirrors getFormattedArmNumber in packages/modules/forms/src/util/score-util.ts
// so the printed/displayed armband is identical between the DOCX export and
// the JSON endpoint. Kept as a local copy rather than a cross-package import
// so this package stays a small, dependency-light pure-computation library.
function formatArmNumber(score: IScoreRegistrationSummary): string {
  return score.arm_number || "";
}

// ---------------
function dogsForLevels(
  scores: IScoreRegistrationSummary[],
  showId: string,
  element: string,
  levels: string[]
): IScoreRegistrationSummary[] {
  return scores
    .filter(
      (score) =>
        JSON.stringify(score.show_id) === JSON.stringify(showId) &&
        score.dog_class?.show_element === element &&
        levels.includes(score.dog_class?.level || "")
    )
    .sort(compareArmbands);
}

// ---------------
function buildDogEntries(
  dogGroups: IScoreRegistrationSummary[][],
  trials: (1 | 2)[]
): IAocScheduleDogEntry[] {
  const isCombined = trials.length === 2;
  const groupDogIdSets = isCombined
    ? dogGroups.map((dogs) => new Set(dogs.map((s) => JSON.stringify(s.dog_id))))
    : [];

  const flat: { score: IScoreRegistrationSummary; trial: 1 | 2; groupIdx: number }[] = [];
  dogGroups.forEach((group, groupIdx) => {
    const trial = trials[groupIdx];
    group.forEach((score) => flat.push({ score, trial, groupIdx }));
  });

  // Secretary request (2026-09-15): combined "Trial 1 & Trial 2" printed
  // lines are sorted purely by armband number across BOTH trials merged
  // together, not Trial 1's dogs fully before Trial 2's. A dog entered in
  // both trials shares the same armband and will simply appear twice,
  // back-to-back, in the merged order - that's expected/desired.
  if (isCombined) {
    flat.sort((a, b) => compareArmbands(a.score, b.score));
  }

  return flat.map(({ score, trial, groupIdx }) => {
    const otherGroupIds = isCombined ? groupDogIdSets[1 - groupIdx] : undefined;
    const soloTrialAsterisk =
      !!otherGroupIds && !otherGroupIds.has(JSON.stringify(score.dog_id));

    return {
      dog_id: String(score.dog_id),
      arm_number: formatArmNumber(score),
      breed: score.breed,
      call_name: score.call_name,
      score_id: String(score._id),
      show_id: String(score.show_id),
      trial,
      solo_trial_asterisk: soloTrialAsterisk,
      checked_in: !!score.checked_in,
      completed: score.status === Constants.score_status.complete || !!score.complete,
      scratched: score.status === Constants.score_status.scratched,
      conflict: score.status === Constants.score_status.conflict || !!(score as any).conflict,
    };
  });
}

// ---------------
// Builds the printed items for one column (one ring, merged across the day's
// two trial shows), advancing a running clock from sessionStart using the
// standard AKC Scentwork per-entry running-time table.
function buildColumnItems(
  event: IEvent,
  element: string,
  groups: IRawGroup[],
  trial2GroupsByCanonical: Map<string, IRawGroup>,
  shows: [IShow, IShow],
  scores: IScoreRegistrationSummary[],
  sessionStart: Date
): IAocScheduleItem[] {
  const [trial1Show, trial2Show] = shows;
  const items: IAocScheduleItem[] = [];
  let clock = new Date(sessionStart);

  const runningMinutesFor = (dogs: IScoreRegistrationSummary[], levels: string[]): number =>
    levels.reduce((sum, level) => {
      const count = dogs.filter((d) => d.dog_class?.level === level).length;
      const perEntry = getRunningTimeMinutes(
        event.sanctioning_club,
        Constants.show_type.scentwork,
        element,
        level
      );
      return sum + count * perEntry;
    }, 0);

  const pushItem = (
    levelLabel: string,
    trials: (1 | 2)[],
    dogGroups: IScoreRegistrationSummary[][],
    minutesToAdvance: number,
    breakBeforeMinutes?: number,
    breakLabel?: string
  ) => {
    if (breakBeforeMinutes) {
      clock = dateFns.addMinutes(clock, breakBeforeMinutes);
    }

    const entryCount = dogGroups.reduce((sum, g) => sum + g.length, 0);
    const trialLabel = trials.length === 2 ? "Trial 1 & Trial 2" : `Trial ${trials[0]}`;

    items.push({
      level_label: levelLabel,
      element,
      trials,
      trial_label: trialLabel,
      start_time: clock.toISOString(),
      entry_count: entryCount,
      break_before_minutes: breakBeforeMinutes,
      break_label: breakLabel,
      dogs: buildDogEntries(dogGroups, trials),
    });

    clock = dateFns.addMinutes(clock, minutesToAdvance);
  };

  for (const group of groups) {
    const trial1Dogs = dogsForLevels(scores, trial1Show._id, element, group.levels);

    if (TRIAL_COMBINED_LEVELS.has(group.canonicalLevel)) {
      const trial2Dogs = dogsForLevels(scores, trial2Show._id, element, group.levels);
      const minutes =
        runningMinutesFor(trial1Dogs, group.levels) + runningMinutesFor(trial2Dogs, group.levels);
      pushItem(
        group.canonicalLevel,
        [1, 2],
        [trial1Dogs, trial2Dogs],
        minutes,
        group.breakBeforeMinutes,
        group.breakLabel
      );
    } else {
      // Separate Trial 1 / Trial 2 blocks. Trial 2's levels/order come from
      // its own ring - normally identical to Trial 1's, but resolved
      // independently in case they ever diverge.
      const trial2Group = trial2GroupsByCanonical.get(group.canonicalLevel);
      const trial2Levels = trial2Group?.levels || group.levels;

      pushItem(
        group.canonicalLevel,
        [1],
        [trial1Dogs],
        runningMinutesFor(trial1Dogs, group.levels),
        group.breakBeforeMinutes,
        group.breakLabel
      );

      const trial2Dogs = dogsForLevels(scores, trial2Show._id, element, trial2Levels);
      pushItem(
        group.canonicalLevel,
        [2],
        [trial2Dogs],
        runningMinutesFor(trial2Dogs, trial2Levels)
        // No break between a level's Trial 1 and Trial 2 blocks.
      );
    }
  }

  return items;
}

// ---------------
// Groups this event's Scentwork shows by calendar day (in the event's
// timezone), sorted chronologically. Within a day, the earlier show_date is
// Trial 1 and the later (trailing) show_date is Trial 2.
function groupShowsByDay(event: IEvent, shows: IShow[]): [IShow, IShow][] {
  const timeZone = event.timezone || "America/Toronto";

  const dateKeyFor = (show: IShow) =>
    new Intl.DateTimeFormat("en-US", { dateStyle: "short", timeZone }).format(
      new Date(show.show_date)
    );

  const byDateKey = new Map<string, IShow[]>();
  for (const show of shows) {
    const key = dateKeyFor(show);
    const existing = byDateKey.get(key);
    if (existing) existing.push(show);
    else byDateKey.set(key, [show]);
  }

  const orderedDayKeys = Array.from(byDateKey.keys()).sort(
    (a, b) =>
      new Date(byDateKey.get(a)![0].show_date).getTime() -
      new Date(byDateKey.get(b)![0].show_date).getTime()
  );

  const result: [IShow, IShow][] = [];
  for (const key of orderedDayKeys) {
    const showsForDay = byDateKey.get(key)!;
    if (showsForDay.length < 2) continue;
    const [trial1, trial2] = [...showsForDay].sort(
      (a, b) => new Date(a.show_date).getTime() - new Date(b.show_date).getTime()
    );
    result.push([trial1, trial2]);
  }

  return result;
}

// ---------------
function dayLabelFor(show: IShow, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", { weekday: "long", timeZone }).format(
    new Date(show.show_date)
  );
}

// ---------------
/**
 * Computes the full interwoven schedule for this event, live from the
 * event's Scentwork shows + scores + each show's configured Ring 1/Ring 2
 * running_order. Days with fewer than 2 rings configured are returned with
 * an empty `columns` array (rather than throwing) so a partially-configured
 * event still renders whatever is ready.
 */
export function computeAocInterwovenSchedule(
  event: IEvent,
  shows: IShow[],
  scores: IScoreRegistrationSummary[]
): IAocScheduleDay[] {
  const timeZone = event.timezone || "America/Toronto";
  const scentworkShows = shows.filter((s) => s.show_type === Constants.show_type.scentwork);
  const dayPairs = groupShowsByDay(event, scentworkShows);

  return dayPairs.map(([trial1Show, trial2Show]): IAocScheduleDay => {
    const ringNames = Array.from(
      new Set([
        ...(trial1Show.rings || []).map((r) => r.ring),
        ...(trial2Show.rings || []).map((r) => r.ring),
      ])
    ).sort();

    const sessionStart = zonedTimeOnSameDay(
      new Date(trial1Show.show_date),
      timeZone,
      SESSION_START_HOUR,
      SESSION_START_MINUTE
    );
    const briefingTime = dateFns.subMinutes(sessionStart, JUDGES_BRIEFING_MINUTES_BEFORE);

    const columns: IAocScheduleColumn[] = [];

    for (const ringName of ringNames) {
      const ring1 = trial1Show.rings?.find((r) => r.ring === ringName);
      const ring2 = trial2Show.rings?.find((r) => r.ring === ringName);

      const judgeEntry1 = ring1?.running_order?.[0];
      const judgeEntry2 = ring2?.running_order?.[0];
      if (!judgeEntry1?.dog_classes?.length) continue;

      const element = judgeEntry1.dog_classes.find(
        (dc) => dc.type === Constants.running_order_dog_class_type.dog_class
      )?.show_element;
      if (!element) continue;

      const groups1 = groupRingDogClasses(judgeEntry1.dog_classes);
      const groups2 = judgeEntry2?.dog_classes
        ? groupRingDogClasses(judgeEntry2.dog_classes)
        : [];
      const groups2ByCanonical = new Map(groups2.map((g) => [g.canonicalLevel, g]));

      const items = buildColumnItems(
        event,
        element,
        groups1,
        groups2ByCanonical,
        [trial1Show, trial2Show],
        scores,
        sessionStart
      );

      columns.push({
        ring: ringName,
        judge_name: judgeEntry1.judge_name || "",
        judge_number: judgeEntry1.judge_number,
        element,
        items,
      });
    }

    return {
      day_label: dayLabelFor(trial1Show, timeZone),
      date: sessionStart.toISOString(),
      briefing_time: briefingTime.toISOString(),
      trial_1_show_id: String(trial1Show._id),
      trial_1_show_status: trial1Show.show_status,
      trial_1_show_status_note: trial1Show.show_status_note,
      trial_2_show_id: String(trial2Show._id),
      trial_2_show_status: trial2Show.show_status,
      trial_2_show_status_note: trial2Show.show_status_note,
      columns,
    };
  });
}
